import React from 'react';
import { Drawer, Box, Typography, IconButton, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { RADIUS } from '../../theme/tokens';

/**
 * BottomSheet
 * Mobile-friendly bottom drawer with rounded top corners, drag handle, and spring enter.
 * On desktop it renders as a standard Dialog-like sheet still anchored to the bottom.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} [title]
 * @param {ReactNode} children
 * @param {string|number} [maxHeight='85vh']
 */
function BottomSheet({ open, onClose, title, children, maxHeight = '85vh', sx = {} }) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: RADIUS.xl,
          borderTopRightRadius: RADIUS.xl,
          maxHeight,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          ...sx,
        },
      }}
      // Framer spring: handled by MUI Drawer's built-in transition
      SlideProps={{ timeout: { enter: 300, exit: 200 } }}
    >
      {/* Drag handle affordance */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: theme.palette.divider,
          }}
        />
      </Box>

      {/* Header */}
      {title && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            pb: 1.5,
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Box>
      )}

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>{children}</Box>
    </Drawer>
  );
}

export default BottomSheet;
